/**
 * Single source of truth for a test type's comparator capability.
 *
 * Historically every consumer re-expanded the `parentSamplesRequired` integer
 * (0 / 1 / 2) into ad-hoc "how many comparators", "which parent roles", and
 * "which sample types are allowed" branches. This accessor centralizes that
 * derivation so call sites read a typed capability object instead of branching
 * on the magic integer. Phase 1 is intentionally behavior-preserving: the
 * values it returns are exactly what the prior inline branches computed.
 */
import type { UiConfig } from '@/config/uiConfig'
import { getTestTypeConfig } from '@/config/uiConfig'
import type { FamilyMemberRelationship } from '@/ordering/canonicalOrder'
import type { CollectionType, TestType } from '@/types/RequisitionFormTypes'

/** Hard ceiling on biological-parent comparators a sequencing tier can request. */
export const MAX_PARENT_COMPARATORS = 2

export interface ComparatorCapabilities {
  /** Product line grouping (e.g. `rwgs`, `urwgs`); empty when the test type is unknown. */
  category: string
  /**
   * Biological-parent comparators the test type requires (= `parentSamplesRequired`,
   * clamped to {@link MAX_PARENT_COMPARATORS}). Tiers have a single required count
   * rather than a min/max range, so only this one value is exposed.
   */
  minComparators: number
  /** Whether the test type accepts any parent comparator samples at all. */
  requiresParentSamples: boolean
  /** Parent roles selectable for this test type (mother/father only; empty for proband-only). */
  allowedParentRoles: FamilyMemberRelationship[]
  /** Collection types allowed for parent samples. */
  parentSampleTypes: CollectionType[]
  /** Collection types allowed for the proband sample. */
  probandSampleTypes: CollectionType[]
}

/** Zero-capability result used when the test type is missing/unknown (proband-only semantics). */
const EMPTY_CAPABILITIES: ComparatorCapabilities = {
  category: '',
  minComparators: 0,
  requiresParentSamples: false,
  allowedParentRoles: [],
  parentSampleTypes: [],
  probandSampleTypes: [],
}

/**
 * Resolve the comparator capability for a test type. Returns a safe
 * zero-capability default when the test type is not present in `config`.
 */
export function getComparatorCapabilities(testType: TestType | string, config?: UiConfig): ComparatorCapabilities {
  const cfg = getTestTypeConfig(testType as TestType, config)
  if (!cfg) return { ...EMPTY_CAPABILITIES }

  const required = cfg.parentSamplesRequired ?? 0
  const allowsParents = required > 0
  return {
    category: cfg.category ?? '',
    minComparators: Math.min(required, MAX_PARENT_COMPARATORS),
    requiresParentSamples: allowsParents,
    allowedParentRoles: allowsParents ? ['MOTHER', 'FATHER'] : [],
    parentSampleTypes: cfg.parentSampleTypes ?? [],
    probandSampleTypes: cfg.probandSampleTypes ?? [],
  }
}
